import { useContext, useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiVideoLine, RiDeleteBinLine } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import { formatModel, hasScene, jobDisplayName, statusText } from "../lib/jobDisplay";
import { HostBridgeContext } from "../hostBridge";

interface VideoGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function VideoGenerationModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: VideoGenerationModalProps) {
  const { t, i18n } = useTranslation();
  const { onAddToScene } = useContext(HostBridgeContext);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isAddingToScene, setIsAddingToScene] = useState(false);

  const isTrainingVideoExport =
    job.type === "training-video-export" || job.type === "training_video_export";

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleApprove = async () => {
    console.log("handleApprove", job);
    try {
      if (job?.type === "video-generation") {
        await handleAddToScene();
        return;
      }
      setIsApproving(true);
      await onApprove(job);
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(job.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(job.id);
      onClose();
    }
  };

  const handleAddToScene = async () => {
    try {
      setIsAddingToScene(true);
      onAddToScene({ jobId: job.id, videoUrl, hasVideo, navId: job.navId });
      onClose();
    } finally {
      setIsAddingToScene(false);
    }
  };

  // Extract video data from job (with same logic as AvatarGenerationModal)
  const videoData = job.result || (job as any).data || {};
  let videoUrl =
    videoData.finalVideoUrl || videoData.videoUrl || videoData.url || job.resultUrl || "";

  // If outputPath exists, construct full URL
  if (!videoUrl && videoData.fileVirtualPath) {
    videoUrl = videoData.fileVirtualPath;
  }

  const isStagedInit = job.type === "staged-video-init" || job.type === "staged_video_init";
  const narrationSegments: Array<{ text?: string; index?: number }> = Array.isArray(
    job.metadata?.narrationSegments
  )
    ? job.metadata.narrationSegments
    : [];
  const totalSegments: number | undefined =
    typeof job.metadata?.totalSegments === "number"
      ? job.metadata.totalSegments
      : narrationSegments.length || undefined;
  // For staged-video-init the segments are the narration source of truth, so
  // skip the metadata.narration_text fallback (it would duplicate segment text).
  const rawNarration = isStagedInit
    ? job.prompt || job.description || ""
    : job.metadata?.narration_text ||
      job.data?.narrationText ||
      job.prompt ||
      job.description ||
      "";
  const narration = typeof rawNarration === "string" ? rawNarration.trim() : "";
  const modelName = formatModel(job.model || job.metadata?.model || job.data?.model, t) || "-";
  const segments = job.result?.segments || [];
  const hasVideo = !!videoUrl && (job.status === "completed" || job.status === "inspect");
  const isCompleted = job.status === "completed";
  const isInspect = job.status === "inspect";
  const isProcessing = job.status === "pending" || job.status === "processing";

  // Debug log
  console.log("[VideoGenerationModal] Job State:", {
    id: job.id,
    status: job.status,
    hasVideo,
    videoUrl,
    narration: narration?.substring(0, 50),
    segmentCount: segments.length,
    isCompleted,
    isInspect,
    isProcessing,
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="fixed top-16 right-4 bottom-4 w-[420px] max-w-[90vw] rounded-lg border border-white/20 bg-white backdrop-blur-xl shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />

            <div className="relative h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-black/5">
                <div className="space-y-0.5">
                  <p className="text-xs tracking-[0.08em] text-gray-500">
                    {job.type === "staged-video-init" || job.type === "staged_video_init"
                      ? t("modal.storyboardPreviewUpper")
                      : job.type === "staged-video-generation" ||
                          job.type === "staged_video_generation"
                        ? t("modal.scenePreviewUpper")
                        : t("modal.videoPreviewUpper")}
                  </p>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {job.type === "staged-video-init" || job.type === "staged_video_init"
                      ? t("jobCard.storyboard")
                      : job.type === "staged-video-generation" ||
                          job.type === "staged_video_generation"
                        ? t("jobCard.sceneGeneration")
                        : jobDisplayName(job, t, i18n)}
                  </h2>
                  {job.type !== "staged-video-init" &&
                    job.type !== "staged_video_init" &&
                    !isTrainingVideoExport &&
                    hasScene(job) && (
                      <p className="text-xs text-gray-500">
                        {t("jobCard.scene")} {job.sceneIndex}
                      </p>
                    )}
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-4 h-4 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-11 h-11 flex items-center justify-center hover:bg-black/5 rounded-lg transition-colors"
                  >
                    <RiCloseLine className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Prompt — render only when narration text actually exists */}
                {narration && !isTrainingVideoExport && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1">
                      {t("modal.promptUpper")}
                    </p>
                    <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-28 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                      {narration}
                    </div>
                  </div>
                )}

                {/* Narration Segments (staged-video-init) */}
                {isStagedInit && narrationSegments.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-500 text-[11px] tracking-[0.08em]">
                        {t("modal.narrationSegmentsUpper")}
                      </p>
                      {typeof totalSegments === "number" && (
                        <span className="text-[11px] font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                          {t("modal.totalSegments", { count: totalSegments })}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {narrationSegments.map((seg, i) => {
                        const idx = typeof seg.index === "number" ? seg.index : i;
                        return (
                          <div
                            key={`${idx}-${i}`}
                            className="p-2 rounded-md bg-gray-50 border border-gray-100"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-md bg-blue-50 border border-blue-100 text-blue-600 text-[10px] font-semibold">
                                {idx + 1}
                              </span>
                              <span className="text-[10px] uppercase tracking-[0.08em] text-gray-400">
                                {t("modal.segment")} {idx + 1}
                              </span>
                            </div>
                            <p className="text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                              {seg.text || ""}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Video Preview */}
                {job.type !== "staged-video-init" && job.type !== "staged_video_init" && (
                  <div>
                    <h3 className="text-[11px] font-semibold text-gray-600 tracking-[0.08em] mb-2">
                      {t("modal.generatedVideoUpper")}
                    </h3>
                    <div
                      className="border border-gray-200 rounded-lg bg-black flex items-center justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {hasVideo ? (
                        <video
                          key={videoUrl}
                          src={videoUrl}
                          controls
                          preload="metadata"
                          className="w-full rounded-lg"
                          style={{ maxHeight: "240px" }}
                        />
                      ) : (
                        <div className="h-40 flex flex-col items-center justify-center gap-2">
                          <RiVideoLine className="w-8 h-8 text-gray-500" />
                          <p className="text-xs text-gray-400">
                            {isProcessing ? t("modal.videoGenerating") : t("modal.noVideoYet")}
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">{t("modal.aiContentWarning")}</p>
                  </div>
                )}

                {/* Parameters */}
                <div
                  className={`grid ${isTrainingVideoExport ? "grid-cols-2" : "grid-cols-3"} gap-3 text-sm`}
                >
                  {!isTrainingVideoExport && (
                    <div>
                      <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                        {t("jobCard.model")}
                      </p>
                      <p className="text-gray-800 font-semibold">{modelName}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.created")}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      {new Date(job.createdAt).toLocaleDateString(
                        i18n.language === "tr" ? "tr-TR" : "en-US"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.status")}
                    </p>
                    <p className="text-gray-800 font-semibold capitalize">
                      {statusText(job.status, t, i18n)}
                    </p>
                  </div>
                </div>

                {job.error && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-sm">
                    <p className="text-red-700 text-[11px] uppercase tracking-[0.08em] mb-1">
                      {t("jobCard.error")}
                    </p>
                    <p className="text-red-700 text-xs leading-snug">{jobErrorText(job, t)}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              {!showHistory && (
                <div className="border-t border-gray-200 px-4 py-3 flex gap-2 bg-white">
                  {!isTrainingVideoExport && (
                    <button
                      onClick={handleDecline}
                      disabled={isDeclining || isApproving || isAddingToScene}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white hover:bg-red-50 text-red-600 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                    >
                      {isDeclining ? t("actions.declining") : t("actions.decline")}
                    </button>
                  )}

                  {(isCompleted || isInspect) && (
                    <button
                      onClick={handleApprove}
                      disabled={isApproving || isDeclining || isAddingToScene}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isApproving ? t("actions.approving") : t("actions.approve")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
